import { buildChartCurrencyFormatters } from '../reports/charts/chartCurrencyFormat';
import {
  CHART_TYPE_BUILDERS,
  buildDonutOption,
  buildFunnelOption,
  getCartesianGrid,
  getLegendConfig,
  getTooltipConfig,
  normalizeChartType,
  resolveChartType,
} from '../reports/charts/echarts/chartTypeRegistry';
import { reportFieldIsCurrencyLike } from '../../utils/reportCurrencyAggregate';
import {
  buildAiChatFieldMeta,
  getAiChatConversionBase,
  parseAiChatChartNumeric,
} from './aiChatCurrencyUtils';

const DEFAULT_COLORS = [
  '#36A2EB',
  '#FF6384',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40',
  '#4BC0C0',
  '#9966FF',
];

/** True when the user asked for a chart/graph visualization (supports plural, e.g. "with charts"). */
export function wantsChartFromQuestion(question) {
  const q = (question || '').toLowerCase();
  return (
    /\b(charts?|graphs?|plots?|visuali[sz]e|visualisation|visualization)\b/i.test(q) ||
    /\b(pie|donut|doughnut|funnel|line|area|bar|column|stacked)\s+(charts?|graphs?|plots?)\b/i.test(
      q
    ) ||
    /\b(with|as|in|using)\s+(a\s+)?(charts?|graphs?|plots?)\b/i.test(q)
  );
}

/** User wants columns/bars plus a line overlay (e.g. win % trend on bars). */
export function wantsMixedBarLineChart(question) {
  const q = (question || '').toLowerCase();
  return (
    /\bmixed\b/.test(q) ||
    /\bcombo\b/.test(q) ||
    /\bbar\s*\+\s*line\b/.test(q) ||
    /\bcolumn\s*\+\s*line\b/.test(q) ||
    (/\b(column|bar)\b/.test(q) && /\b(line|trend)\b/.test(q)) ||
    (/\b(line|trend)\b/.test(q) &&
      /\b(win\s*%|win\s*rate|win\s*percentage|percentage)\b/.test(q)) ||
    (/\bshow\b/.test(q) && /\b(line|trend)\b/.test(q) && /\b(bar|column)\b/.test(q))
  );
}

/** Map user phrasing to report chart types (same set as ReportChart / ECharts). */
export function detectChartTypeFromQuestion(question) {
  const q = (question || '').toLowerCase();
  if (/\bfunnel\b/.test(q)) return 'funnel';
  if (/\b(pie|donut|doughnut)\b/.test(q)) return 'donut';
  if (wantsMixedBarLineChart(question)) return 'mixed';
  if (/\barea\s+(chart|graph|plot)\b/.test(q) || (/\barea\b/.test(q) && /\b(chart|graph|plot)\b/.test(q))) {
    return 'area';
  }
  if (/\bstacked\s+(horizontal\s+)?bar\b/.test(q) || /\bstacked\s+bar\s+chart\b/.test(q)) {
    return 'stackedBar';
  }
  if (/\bstacked\s+(column|chart)\b/.test(q) || (/\bstacked\b/.test(q) && /\b(chart|graph|plot)\b/.test(q))) {
    return 'stackedColumn';
  }
  if (
    /\bhorizontal\s+bar\b/.test(q) ||
    (/\bbar\s+chart\b/.test(q) && !/\bcolumn\b/.test(q))
  ) {
    return 'horizontalBar';
  }
  if (/\bcolumn\s+chart\b/.test(q)) return 'bar';
  // Pure line only when not also asking for columns/bars (trend-only phrasing)
  if (
    /\bline\s+(chart|graph|plot)\b/.test(q) ||
    (/\b(line|trend)\b/.test(q) && /\b(chart|graph|plot)\b/.test(q) && !/\b(column|bar)\b/.test(q))
  ) {
    return 'line';
  }
  return 'bar';
}

export function normalizeAiChartType(chartType) {
  const raw = String(chartType || 'bar').trim().toLowerCase();
  if (raw === 'doughnut' || raw === 'pie') return 'donut';
  return normalizeChartType(chartType);
}

function unwrapChartCellValue(val) {
  if (val != null && typeof val === 'object' && !Array.isArray(val) && 'value' in val) {
    return val.value;
  }
  return val;
}

function isNumericValue(val) {
  const raw = unwrapChartCellValue(val);
  if (typeof raw === 'number' && Number.isFinite(raw)) return true;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = parseFloat(raw.replace(/[₹$,%\s]/g, ''));
    return Number.isFinite(n);
  }
  return false;
}

function parseNumeric(val) {
  const raw = unwrapChartCellValue(val);
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const n = parseFloat(raw.replace(/[₹$,%\s]/g, ''));
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function isIdLike(col) {
  return col === 'id' || col === 'ownerid' || col.toLowerCase().endsWith('_id');
}

/** Win %, allocation %, etc. — rendered as line on secondary axis in mixed charts. */
function isPercentColumn(col, columnLabels, rows) {
  const label = `${columnLabels?.[col] || ''} ${col}`.toLowerCase();
  if (/(?:%|percent|percentage|win\s*rate|win\s*%|win_rate|winrate)/.test(label)) {
    return true;
  }
  if (/\brate\b/.test(label) && !/\bclosed\b/.test(label)) return true;
  const values = rows.map((row) => parseNumeric(row[col]));
  if (!values.length) return false;
  const allBounded = values.every((v) => v >= 0 && v <= 100);
  if (allBounded && /\b(win|ratio|share|pct)\b/.test(label)) return true;
  return false;
}

function inferChartTypeFromSeries(extracted, requestedType) {
  if (!extracted?.series?.length) return normalizeAiChartType(requestedType);
  const lineSeries = extracted.series.filter((s) => s.seriesType === 'line');
  const barSeries = extracted.series.filter((s) => s.seriesType === 'bar');
  if (lineSeries.length > 0 && barSeries.length > 0) return 'mixed';
  const ct = normalizeAiChartType(requestedType);
  if (ct === 'line' && barSeries.length > 0 && lineSeries.length > 0) return 'mixed';
  if (ct === 'mixed') return 'mixed';
  return ct;
}

function buildChartConfigForExtracted(extracted, chartType) {
  const ct = inferChartTypeFromSeries(extracted, chartType);
  const seriesTypes = Object.fromEntries(
    (extracted.series || []).map((s) => [s.fieldName, s.seriesType || 'bar'])
  );
  const lineMeta = (extracted.series || []).find((s) => s.seriesType === 'line');
  return {
    chartType: ct,
    chartEngine: 'echarts',
    legendPosition: 'top',
    showTooltips: true,
    showLabels: false,
    seriesTypes,
    yAxisLabelSecondary: lineMeta?.name || 'Win %',
  };
}

/**
 * Build domain series from flat SQL rows (same shape extractChartSeries returns).
 */
export function extractSeriesFromRows(rows, columnLabels, objectName, currencyCtx) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const first = rows[0];
  const columns =
    typeof first === 'object' && first !== null ? Object.keys(first) : [];
  if (columns.length < 2) return null;

  const colIsNumeric = (col) => rows.some((row) => isNumericValue(row[col]));

  const labelCol =
    columns.find((c) => (c === 'owner' || c === 'name') && !colIsNumeric(c)) ||
    columns.find((c) => !isIdLike(c) && !colIsNumeric(c)) ||
    columns[0];

  const numericCols = columns.filter((c) => c !== labelCol && colIsNumeric(c));
  if (!numericCols.length) return null;

  const labels = rows.map((row) => {
    const v = row[labelCol];
    const raw = unwrapChartCellValue(v);
    return raw != null ? String(raw) : '';
  });

  const series = numericCols.map((col, index) => {
    const color = DEFAULT_COLORS[index % DEFAULT_COLORS.length];
    const fieldMeta = buildAiChatFieldMeta(col, rows, objectName);
    const data = rows.map((row) =>
      parseAiChatChartNumeric(row, col, rows, objectName, currencyCtx)
    );
    const singleSeries = numericCols.length === 1;
    const asLine = isPercentColumn(col, columnLabels, rows);
    const asCurrency =
      !asLine &&
      reportFieldIsCurrencyLike(fieldMeta) &&
      Boolean(getAiChatConversionBase(currencyCtx));
    return {
      fieldName: col,
      name: columnLabels?.[col] || col.replace(/_/g, ' '),
      data,
      color,
      dataType: asLine ? 'percent' : asCurrency ? 'currency' : 'number',
      perCategoryColors:
        singleSeries && !asLine
          ? labels.map((_, i) => DEFAULT_COLORS[i % DEFAULT_COLORS.length])
          : undefined,
      seriesType: asLine ? 'line' : 'bar',
    };
  });

  if (labels.length === 0 || series.every((s) => s.data.every((n) => n === 0))) {
    return null;
  }

  const primaryValueCol = numericCols[0];
  return {
    labels,
    series,
    xAxisLabel: columnLabels?.[labelCol] || labelCol.replace(/_/g, ' '),
    yAxisLabel: columnLabels?.[primaryValueCol] || primaryValueCol.replace(/_/g, ' '),
  };
}

function buildPieItemsFromRows(rows, columnLabels, objectName, currencyCtx) {
  const extracted = extractSeriesFromRows(rows, columnLabels, objectName, currencyCtx);
  if (!extracted?.labels?.length || !extracted.series?.[0]) return null;
  const values = extracted.series[0].data;
  const items = extracted.labels.map((name, i) => ({
    name,
    value: values[i] ?? 0,
    color: DEFAULT_COLORS[i % DEFAULT_COLORS.length],
  }));
  if (items.every((item) => !item.value)) return null;
  return { items };
}

function wrapOption(partial, chart, useGrid, extracted, currencyFormatters = null) {
  return {
    title: { show: false, text: chart.title || '' },
    color: DEFAULT_COLORS,
    grid: useGrid
      ? getCartesianGrid(chart, extracted, {
          horizontalCategories:
            partial?.yAxis?.type === 'category' && partial?.yAxis?.inverse,
        })
      : undefined,
    legend: partial.legend ?? getLegendConfig(chart),
    tooltip: partial.tooltip ?? getTooltipConfig(chart, currencyFormatters, extracted),
    ...partial,
  };
}

function buildCurrencyFormattersForExtracted(extracted, rows, objectName, currencyCtx) {
  if (!extracted?.series?.length || !currencyCtx?.multiCurrencyEnabled) return null;
  const primary = extracted.series.find((s) => s.dataType === 'currency') || extracted.series[0];
  if (!primary) return null;
  const fieldMeta = {
    ...buildAiChatFieldMeta(primary.fieldName, rows, objectName),
    aggregation: 'sum',
  };
  if (!reportFieldIsCurrencyLike(fieldMeta)) return null;
  return buildChartCurrencyFormatters({
    measureField: fieldMeta,
    baseCurrencyCode: currencyCtx.baseCurrencyCode,
    multiCurrencyEnabled: currencyCtx.multiCurrencyEnabled,
  });
}

/**
 * Build a full ECharts option for AI SQL result rows.
 */
export function buildAiChatEChartsOption(
  rows,
  columnLabels,
  chartType = 'bar',
  { objectName, currencyCtx } = {}
) {
  const normalizedType = normalizeAiChartType(chartType);

  if (normalizedType === 'donut' || normalizedType === 'funnel') {
    const chart = {
      chartType: normalizedType,
      chartEngine: 'echarts',
      legendPosition: 'top',
      showTooltips: true,
      showLabels: false,
    };
    const pieData = buildPieItemsFromRows(rows, columnLabels, objectName, currencyCtx);
    if (!pieData?.items?.length) return null;
    const pieExtracted = extractSeriesFromRows(rows, columnLabels, objectName, currencyCtx);
    const currencyFormatters = buildCurrencyFormattersForExtracted(
      pieExtracted,
      rows,
      objectName,
      currencyCtx
    );
    const partial =
      normalizedType === 'funnel'
        ? buildFunnelOption(pieData, chart)
        : buildDonutOption(pieData, chart);
    return wrapOption(partial, chart, false, null, currencyFormatters);
  }

  const extracted = extractSeriesFromRows(rows, columnLabels, objectName, currencyCtx);
  if (!extracted?.labels?.length || !extracted.series?.length) return null;

  const chart = buildChartConfigForExtracted(extracted, chartType);
  const ct = resolveChartType(chart);
  const currencyFormatters = buildCurrencyFormattersForExtracted(
    extracted,
    rows,
    objectName,
    currencyCtx
  );
  const builder = CHART_TYPE_BUILDERS[ct] || CHART_TYPE_BUILDERS.bar;
  const partial = builder(extracted, chart, currencyFormatters);
  return wrapOption(partial, chart, true, extracted, currencyFormatters);
}
